/**
 * Dependency-free, versioned, default-deny consent storage.
 *
 * Categories tracked:
 * - `analytics`: Google Analytics (GA4) + PostHog product analytics.
 * - `visitorIdentification`: RB2B company/visitor identification.
 *
 * Persistence is a single localStorage record. Every read is validated —
 * missing storage, corrupt JSON, an old/unknown `version`, or any field with
 * an unexpected shape is treated as "no valid consent" and fails closed to
 * `DEFAULT_CONSENT` (every category denied, `decided: false`) so the banner
 * re-prompts rather than silently trusting stale or tampered data.
 */

export const CONSENT_STORAGE_KEY = 'atease_cookie_consent';

// Bump when the stored shape or category set changes so previously stored
// records (old version) are treated as invalid and re-prompted, even if
// they happen to parse as valid JSON.
export const CONSENT_VERSION = 1;

export const DEFAULT_CONSENT = Object.freeze({
  version: CONSENT_VERSION,
  decided: false,
  analytics: false,
  visitorIdentification: false,
  updatedAt: null,
});

const isBoolean = (value) => typeof value === 'boolean';

/**
 * Structural validation for a parsed consent record. Fails closed: anything
 * unexpected returns false rather than trying to coerce/repair the record.
 * @param {unknown} record
 * @returns {boolean}
 */
export function isValidConsent(record) {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return false;
  if (record.version !== CONSENT_VERSION) return false;
  if (!isBoolean(record.decided)) return false;
  if (!isBoolean(record.analytics)) return false;
  if (!isBoolean(record.visitorIdentification)) return false;
  return true;
}

/**
 * Reads consent from localStorage. Default-deny + fail-closed: any error
 * (storage unavailable, corrupt JSON, invalid/old shape) returns a fresh
 * copy of `DEFAULT_CONSENT` instead of throwing or trusting partial data.
 * @returns {typeof DEFAULT_CONSENT}
 */
export function loadConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CONSENT };

    const parsed = JSON.parse(raw);
    if (!isValidConsent(parsed)) return { ...DEFAULT_CONSENT };

    return {
      version: CONSENT_VERSION,
      decided: parsed.decided,
      analytics: parsed.analytics,
      visitorIdentification: parsed.visitorIdentification,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : null,
    };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

/**
 * Persists an explicit consent decision. Always marks `decided: true` — this
 * is only called from an explicit user action (Accept All, Reject
 * Non-Essential, Save Preferences), never implicitly.
 * @param {{ analytics?: boolean, visitorIdentification?: boolean }} categories
 * @returns {typeof DEFAULT_CONSENT} The persisted record.
 */
export function saveConsent(categories) {
  const record = {
    version: CONSENT_VERSION,
    decided: true,
    analytics: !!categories?.analytics,
    visitorIdentification: !!categories?.visitorIdentification,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage unavailable (private browsing / quota exceeded) — consent
    // still applies in-memory for this page load via ConsentContext state,
    // it just won't persist across reloads.
  }

  return record;
}

/** Removes any stored consent decision, e.g. for tests or full data resets. */
export function clearConsent() {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
}
